import BaseCard from "./base-card";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { formatToPKR } from "@/lib/helper";
import { FacilitySchedule } from "@/types/facility/schedule";
import { MemberTableDatatypes } from "@/types/member";
import { Calendar, Users, MapPin, Sparkles } from "lucide-react";
import React, { useRef, useEffect, useState } from "react";
import { parseAsUTC } from "@/lib/helper";

export type POSEvent = FacilitySchedule & {
  // Additional fields for POS display
  displayPrice?: number;
  priceType?: "regular" | "plan" | "special";
};

interface EventCardProps {
  event: POSEvent;
  disabled?: boolean;
  customer: MemberTableDatatypes | null;
  onSelect: () => void;
}

/**
 * Calculate the best price for an event based on:
 * 1. Special pricing (if current date falls within date range)
 * 2. Plan-based pricing (if customer has matching membership plan)
 * 3. Regular price (fallback)
 * Returns the lowest applicable price
 */
export const calculateEventPrice = (
  event: FacilitySchedule,
  customer: MemberTableDatatypes | null,
): {
  price: number;
  priceType: "regular" | "plan" | "special" | "group";
  specialPricingName?: string;
  groupPricingName?: string;
} => {
  const regularPrice = event.general_info?.price || 0;
  const today = parseAsUTC(new Date());
  today.setHours(0, 0, 0, 0);

  // Collect all applicable special pricing (date-based)
  const applicableSpecialPrices: Array<{ price: number; name?: string }> = [];
  event.special_pricing?.forEach((sp) => {
    if (!sp.date_from || !sp.date_to) return;

    const dateFrom = parseAsUTC(sp.date_from);
    const dateTo = parseAsUTC(sp.date_to);
    dateFrom.setHours(0, 0, 0, 0);
    dateTo.setHours(0, 0, 0, 0);

    if (today >= dateFrom && today <= dateTo) {
      applicableSpecialPrices.push({ price: sp.price, name: sp.name });
    }
  });

  // Find the lowest special price if any
  const lowestSpecialPrice =
    applicableSpecialPrices.length > 0
      ? applicableSpecialPrices.reduce((min, current) =>
          current.price < min.price ? current : min,
        )
      : null;

  // Collect all applicable plan-based pricing if customer is selected
  const applicablePlanPrices: Array<{ price: number }> = [];
  if (customer?.membership_plans && event.plan_pricing) {
    const customerPlanIds = customer.membership_plans.map(
      (mp) => mp.membership_plan_id,
    );
    event.plan_pricing.forEach((pp) => {
      if (
        pp.membership_ids?.some((id) => customerPlanIds.includes(Number(id)))
      ) {
        applicablePlanPrices.push({ price: pp.price });
      }
    });
  }

  // Find the lowest plan price if any
  const lowestPlanPrice =
    applicablePlanPrices.length > 0
      ? applicablePlanPrices.reduce((min, current) =>
          current.price < min.price ? current : min,
        )
      : null;

  // Collect all applicable group-based pricing if customer is selected
  const applicableGroupPrices: Array<{ price: number }> = [];
  if (customer?.member_group_id && event.group_pricing) {
    event.group_pricing.forEach((gp) => {
      if (Number(gp.group_id) === customer.member_group_id) {
        applicableGroupPrices.push({ price: gp.price });
      }
    });
  }

  // Find the lowest group price if any
  const lowestGroupPrice =
    applicableGroupPrices.length > 0
      ? applicableGroupPrices.reduce((min, current) =>
          current.price < min.price ? current : min,
        )
      : null;

  // Collect all prices to compare (regular, lowest special, lowest plan, lowest group)
  const prices: Array<{
    price: number;
    type: "regular" | "plan" | "special" | "group";
    pricingName?: string;
  }> = [{ price: regularPrice, type: "regular" }];

  if (lowestSpecialPrice !== null) {
    prices.push({
      price: lowestSpecialPrice.price,
      type: "special",
      pricingName: lowestSpecialPrice.name,
    });
  }

  if (lowestPlanPrice !== null) {
    prices.push({ price: lowestPlanPrice.price, type: "plan" });
  }

  if (lowestGroupPrice !== null) {
    prices.push({
      price: lowestGroupPrice.price,
      type: "group",
    });
  }

  // Find the absolute lowest price
  const lowestPrice = prices.reduce((min, current) => {
    if (current.price < min.price) return current;
    return min;
  }, prices[0]);

  return {
    price: lowestPrice.price,
    priceType: lowestPrice.type,
    specialPricingName:
      lowestPrice.type === "special" ? lowestPrice.pricingName : undefined,
    groupPricingName: lowestPrice.type === "group" ? "Group" : undefined,
  };
};

const EventCard = ({ event, disabled, customer, onSelect }: EventCardProps) => {
  const { price, priceType, specialPricingName, groupPricingName } =
    calculateEventPrice(event, customer);
  const regularPrice = event.general_info?.price || 0;
  const remainingCapacity = event.general_info?.remaining_capacity ?? 0;
  const maxCapacity = event.general_info?.max_attendees ?? 0;
  const eventName = event.general_info?.name || "Unnamed Event";
  const facilityName = event.general_info?.facility_name || "";
  const startDate = event.general_info?.start_date;
  const endDate = event.general_info?.end_date;
  const location = event.general_info?.location;
  const nameRef = useRef<HTMLParagraphElement>(null);
  const [isNameTruncated, setIsNameTruncated] = useState(false);

  // Format date range with better formatting
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year:
        date.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined,
    });
  };

  const dateRange =
    startDate && endDate
      ? `${formatDate(startDate)} - ${formatDate(endDate)}`
      : startDate
        ? formatDate(startDate)
        : "";

  // Determine capacity status
  const isSoldOut = remainingCapacity === 0;
  const isLowCapacity = remainingCapacity > 0 && remainingCapacity <= 3;
  // const capacityPercentage = maxCapacity > 0 ? (remainingCapacity / maxCapacity) * 100 : 0; // Removed

  // Check if there's a discount
  const hasDiscount = priceType !== "regular" && price < regularPrice;
  const discountAmount = hasDiscount ? regularPrice - price : 0;
  const discountPercentage = hasDiscount
    ? Math.round((discountAmount / regularPrice) * 100 * 10) / 10
    : 0;

  // Check if event name is truncated
  useEffect(() => {
    if (nameRef.current) {
      const pElement = nameRef.current.querySelector("p");
      if (pElement) {
        const isTruncated = pElement.scrollHeight > pElement.clientHeight;
        setIsNameTruncated(isTruncated);
      }
    }
  }, [eventName]);

  return (
    <BaseCard
      onClick={onSelect}
      disabled={disabled || isSoldOut}
      ariaLabel={`Add ${eventName}`}
      className="flex-col" // Ensure flex-col for consistent layout
    >
      {/* Header with neutral background */}
      <div className={cn("relative h-20 w-full overflow-hidden bg-gray-100")}>
        {/* Decorative pattern overlay */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.3)_1px,transparent_1px)] bg-[length:16px_16px]" />
        </div>

        {/* Content */}
        <div className="relative flex h-full w-full flex-col items-center justify-center px-3 py-2 text-black">
          <div className="mb-1 rounded-full bg-white/20 p-1.5 backdrop-blur-sm">
            <Calendar className="h-4 w-4" />
          </div>
          <p className="text-[10px] font-semibold text-center leading-tight drop-shadow-sm">
            {dateRange}
          </p>
        </div>

        {/* Badges overlay */}
        <div className="absolute left-1.5 top-1.5 flex flex-col gap-1">
          {priceType === "special" && (
            <Badge
              variant="secondary"
              className="bg-amber-500/90 text-black border-0 shadow-sm backdrop-blur-sm text-[10px] px-1.5 py-0 h-5 max-w-[120px] flex items-center gap-0.5"
            >
              <Sparkles className="h-2.5 w-2.5 flex-shrink-0" />
              <span className="truncate">
                {specialPricingName || "Special"}
              </span>
            </Badge>
          )}
          {priceType === "plan" && (
            <Badge
              variant="outline"
              className="border-green-300/80 bg-green-500/90 text-black shadow-sm backdrop-blur-sm text-[10px] px-1.5 py-0 h-5"
            >
              Plan
            </Badge>
          )}
          {priceType === "group" && (
            <Badge
              variant="secondary"
              className="bg-blue-500/90 text-black border-0 shadow-sm backdrop-blur-sm text-[10px] px-1.5 py-0 h-5 max-w-[120px] flex items-center gap-0.5"
            >
              <Users className="h-2.5 w-2.5 flex-shrink-0" />
              <span className="truncate">{groupPricingName || "Group"}</span>
            </Badge>
          )}
          {isSoldOut && (
            <Badge
              variant="destructive"
              className="bg-red-500/90 text-black border-0 shadow-sm backdrop-blur-sm text-[10px] px-1.5 py-0 h-5"
            >
              Sold Out
            </Badge>
          )}
        </div>
      </div>

      {/* Content section */}
      <div className="flex flex-col gap-1.5 p-2.5">
        {/* Event name with conditional tooltip */}
        <div ref={nameRef as React.RefObject<HTMLDivElement>}>
          {isNameTruncated ? (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <p className="line-clamp-2 text-sm font-semibold text-gray-900 capitalize leading-snug group-hover:text-primary transition-colors cursor-help">
                    {eventName}
                  </p>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  className="max-w-xs break-words text-xs capitalize"
                >
                  {eventName}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <p className="line-clamp-2 text-sm font-semibold text-gray-900 capitalize leading-snug group-hover:text-primary transition-colors">
              {eventName}
            </p>
          )}
        </div>

        {/* Price section */}
        <div className="flex items-baseline ">
          <div className="flex flex-col gap-0">
            {hasDiscount && (
              <p className="text-[11px] text-gray-400 line-through leading-none">
                {formatToPKR(regularPrice)}
              </p>
            )}
            <p className="text-base font-bold text-primary leading-none">
              {formatToPKR(price)}
            </p>
          </div>
          {hasDiscount && (
            <Badge
              variant="secondary"
              className="bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0 h-4 leading-none"
            >
              -{discountPercentage}%
            </Badge>
          )}
        </div>

        {/* Capacity info */}
        <div className="flex flex-wrap gap-1 items-center  pt-1 border-t border-gray-100">
          <div className="flex items-center gap-x-1">
            <Users
              className={cn(
                "h-3 w-3",
                isSoldOut
                  ? "text-red-500"
                  : isLowCapacity
                    ? "text-orange-500"
                    : "text-gray-400",
              )}
            />

            <span
              className={cn(
                "text-[11px] font-medium ",
                isSoldOut
                  ? "text-red-500"
                  : isLowCapacity
                    ? "text-orange-500"
                    : "text-gray-400",
              )}
            >
              Available Seats:
            </span>
          </div>
          <span
            className={cn(
              "text-[11px] font-medium",
              isSoldOut
                ? "text-red-600"
                : isLowCapacity
                  ? "text-orange-600"
                  : "text-gray-600",
            )}
          >
            {remainingCapacity}
          </span>
        </div>
      </div>
    </BaseCard>
  );
};

export default EventCard;
